import type { FC, ComponentProps, ComponentType, Dispatch, ReactNode, SetStateAction } from 'react';
import React, { Fragment, useState } from 'react';
import { styled } from '@compiled/react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import Flex from '@atlaskit/primitives/components/flex';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import Stack from '@atlaskit/primitives/stack';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import { media } from '@atlaskit/primitives/responsive/media';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop.ts';
import {
	VIEW_PAGE_CONTENT_HEADER_EXPERIENCE,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader.tsx';
import { FabricEligibilityFragmentProvider } from '@confluence/edit-button/entry-points/FabricEligibilityFragmentProvider.ts';
import {
	Breadcrumbs,
	FixedSpaceHeaderContainer,
} from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
} from '@confluence/web-item/entry-points/webItemIDs.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries.ts';
import { usePageHeightWidthObserver } from '@confluence/page-cover-picture/entry-points/usePageHeightWidthObserver.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { ContentHeaderPresence } from '@confluence/team-presence/entry-points/ContentHeaderPresence.tsx';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader.ts';

import { ContentHeaderUnifiedQuery } from './ContentHeaderUnifiedQuery.graphql.ts';
import {
	type ContentHeaderUnifiedQuery$data,
	type ContentHeaderUnifiedQuery$variables,
} from './__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import type {
	ContentHeader_content$data,
	ContentHeader_content$key,
} from './__generated__/ContentHeader_content.graphql.ts';
import type {
	ContentHeader_space$data,
	ContentHeader_space$key,
} from './__generated__/ContentHeader_space.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from './defaultQueryVariables.js';
import { PageMetadataAtl } from './PageMetadataAtl.jsx';
import { PageMetadataBanner } from './PageMetadataBanner.jsx';
import { SystemContentMetadata } from './SystemContentMetadata.jsx';
import { GroupedPageButtons } from './GroupedPageButtons.jsx';
import { CONNECT_APP_WEB_FRAGMENT_METRIC } from './perf.config.js';
import { checkIsFabricPage } from './content-title/ContentTitle.tsx';

const ContentClassificationTagLoader = LoadableAfterPaint({
	name: 'ContentClassificationTag',
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-confluencedata-classificationentry-pointsContentClassificationTag" */ '@confluence/data-classification/entry-points/ContentClassificationTag.ts'
			)
		).ContentClassificationTag,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpacedActionItem = styled.div({
	marginLeft: token('space.050'),
});

const breadcrumbsAreaWrapper = xcss({
	flexWrap: 'nowrap',
	flex: '0 1 auto',
	minWidth: 'fit-content',
	gap: 'space.050',
	overflow: 'hidden',

	[media.above.xs]: {
		minWidth: '96px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const Metadata = styled.div((props: MetadataProps) => ({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	overflow: 'hidden', // needed to hide unpublished lozenge below certain header width
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: props.shouldDisplayClassification ? '20px' : '30px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .content-metadata-jira': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: props.shouldDisplayClassification ? '-5px' : '0',
	},
}));

const verticalSeparatorStyles = xcss({
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
	height: '16px',
	margin: 'space.075',
});

const rightSideHeaderStyles = xcss({
	marginLeft: 'auto',
	alignItems: 'center',
});

const VerticalSeparator = () => <Box xcss={verticalSeparatorStyles} />;

SpacedActionItem.displayName = 'SpacedActionItem';

const EXCLUDED_SYSTEM_IDS = [
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
];

const SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS = [CONTENT_METADATA_PAGE_RESTRICTIONS_ID];

const ContentHeaderContentFragment: GraphQLTaggedNode = graphql`
	fragment ContentHeader_content on Content
	@argumentDefinitions(
		includeAccessButton: { type: "Boolean!", defaultValue: false }
		includeFabricEditorEligibility: { type: "Boolean!", defaultValue: false }
	) {
		id
		type
		subType
		title
		status
		metadata {
			lastModifiedDate
			frontend {
				fabricEditorSupported
			}
		}
		...useFabricEligibilityMigrationQuery_content
			@alias(as: "fabricEditorEligibility")
			@include(if: $includeFabricEditorEligibility)
		...ContentTypesHeader_content @alias(as: "contentTypesHeader")
		...EditContentButton_content @alias(as: "editContentButton")
		...useShareAndRestrictButtonData_content
			@alias(as: "shareAndRestrictButtonData")
			@include(if: $includeAccessButton)
	}
`;

const ContentHeaderSpaceFragment: GraphQLTaggedNode = graphql`
	fragment ContentHeader_space on Space {
		id
		theme {
			themeKey
		}
	}
`;

type MetadataProps = {
	shouldDisplayClassification: boolean;
};

type RenderContentHeaderProps = {
	contentType: string | null | undefined;
	contentTitle: string | null | undefined;
	isFabricPage: boolean;
	loading: boolean;
	subType: string | null | undefined;
	lastModifiedDate: string | null | undefined;
	isThemed: boolean;
	isContentToolsForcedOpen: boolean;
	setIsContentToolsForcedOpen: Dispatch<SetStateAction<boolean>>;
	isArchived: boolean;
};

type RenderBreadcrumbsProps = {
	contentTitle: string | null | undefined;
};

type ContentHeaderProps = {
	spaceKey: string;
	contentId: string;
	actionItems?: ComponentType<any>[];
	versionOverride?: number;
	className?: string;
	showMetadataItems?: boolean;
	isContentTypePublicLinkEligible?: boolean;
	useDocumentContract?: boolean;
	contentFragmentRef?: ContentHeader_content$key | null;
	spaceFragmentRef?: ContentHeader_space$key | null;
	publicLinkPage?: ComponentProps<typeof ContentTypesHeader>['publicLinkPage'];
};

type ContentHeaderFragmentReaderProps = {
	contentFragmentRef: ContentHeader_content$key;
	spaceFragmentRef: ContentHeader_space$key;
	children: (props: {
		contentNode: ContentHeader_content$data;
		space: ContentHeader_space$data;
	}) => ReactNode;
};

const ContentHeaderFragmentReader: FC<ContentHeaderFragmentReaderProps> = ({
	contentFragmentRef,
	spaceFragmentRef,
	children,
}) =>
	children({
		contentNode: useFragment(ContentHeaderContentFragment, contentFragmentRef),
		space: useFragment(ContentHeaderSpaceFragment, spaceFragmentRef),
	});

export const ContentHeader: FC<ContentHeaderProps> = ({
	spaceKey,
	contentId,
	actionItems = [],
	versionOverride = null,
	showMetadataItems = true,
	isContentTypePublicLinkEligible = false,
	useDocumentContract = false,
	contentFragmentRef,
	spaceFragmentRef,
	publicLinkPage,
}) => {
	const [isContentToolsForcedOpen, setIsContentToolsForcedOpen] = useState<boolean>(false);

	const { isExternalCollaborator } = useIsExternalCollaborator();
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});

	const { shouldDisplayClassification } = contentClassificationMetadata;

	const leftRightPaddingOffset = -37;
	const widthLimitForMetadata = 1000;

	const { width } = usePageHeightWidthObserver(false, leftRightPaddingOffset, false);

	const renderBreadcrumbs = ({ contentTitle }: RenderBreadcrumbsProps) => {
		const shouldRenderMetadataForPageWidth = () => {
			return width > widthLimitForMetadata;
		};

		return (
			<Flex alignItems="center" xcss={breadcrumbsAreaWrapper} testId="breadcrumb">
				<Breadcrumbs contentId={contentId} spaceKey={spaceKey} contentTitle={contentTitle || ''} />
				<Inline alignBlock="center">
					{showMetadataItems && shouldRenderMetadataForPageWidth() && (
						<Metadata
							data-testid="metadata-wrapper"
							shouldDisplayClassification={shouldDisplayClassification}
						>
							<SystemContentMetadata
								key={`metadata-${contentId}`}
								contentId={contentId}
								versionOverride={versionOverride}
								excludeIds={EXCLUDED_SYSTEM_IDS}
							/>
							<PageMetadataBanner
								key={`banner-${contentId}`}
								contentId={contentId}
								versionOverride={versionOverride}
							/>
						</Metadata>
					)}
				</Inline>
			</Flex>
		);
	};

	const renderContentHeader = ({
		contentType,
		contentTitle,
		loading,
		subType,
		lastModifiedDate,
		isContentToolsForcedOpen,
		setIsContentToolsForcedOpen,
		isArchived,
		isFabricPage,
	}: RenderContentHeaderProps) => {
		return (
			<Fragment>
				{renderBreadcrumbs({
					contentTitle,
				})}
				<Box testId="content-classification-tag">
					<ContentClassificationTagLoader
						contentId={contentId}
						contentType={contentType || null}
						contentClassificationMetadata={contentClassificationMetadata}
						isEditMode={false}
					/>
				</Box>
				{showMetadataItems && <PageMetadataAtl contentId={contentId} />}

				<Flex alignItems="center" xcss={rightSideHeaderStyles}>
					{!isArchived && (
						<Flex alignItems="center">
							<ContentHeaderPresence
								contentId={contentId}
								contentType={contentType}
								analyticSource="viewPageScreen"
							/>
							<VerticalSeparator />
						</Flex>
					)}
					<GroupedPageButtons
						contentId={contentId}
						version={versionOverride}
						includeSystemIds={SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS}
						spaceKey={spaceKey}
						contentType={contentType}
						contentSubType={subType}
						isExternalCollaborator={isExternalCollaborator}
						lastModifiedDate={lastModifiedDate}
						showEdit={subType !== 'live'}
						isContentToolsForcedOpen={isContentToolsForcedOpen}
						setIsContentToolsForcedOpen={setIsContentToolsForcedOpen}
						isArchived={isArchived}
						isFabricPage={isFabricPage}
					/>
				</Flex>

				{actionItems?.map((ActionItem: ComponentType<any>, index) => {
					const actionItemProps = {
						contentId,
						spaceKey,
						contentType,
					};
					return (
						<SpacedActionItem
							data-testid="spacedActionItemTestId"
							key={ActionItem?.displayName || index}
						>
							<ActionItem {...actionItemProps} disabled={loading} />
						</SpacedActionItem>
					);
				})}
				<ExperienceSuccess
					name={VIEW_PAGE_CONTENT_HEADER_EXPERIENCE}
					attributes={{ contentId, contentType, subType, isFabricPage, isContentWrapper: false }}
				/>
			</Fragment>
		);
	};

	const renderContentHeaderData = ({
		contentNode,
		spaceIsThemed,
		resolvedPublicLinkPage,
		loading,
	}: {
		contentNode:
			| ContentHeader_content$data
			| NonNullable<NonNullable<ContentHeaderUnifiedQuery$data['content']>['nodes']>[0];
		spaceIsThemed: boolean;
		resolvedPublicLinkPage: ComponentProps<typeof ContentTypesHeader>['publicLinkPage'];
		loading: boolean;
	}) => {
		const contentType = contentNode?.type;
		const lastModifiedDate = contentNode?.metadata?.lastModifiedDate;
		const subType = contentNode?.subType;
		const contentTitle = contentNode?.title;
		const isFabricPage = checkIsFabricPage(contentNode);
		const isArchived = contentNode?.status === 'archived';
		const fabricEligibilityFragmentRef =
			useDocumentContract &&
			contentNode &&
			'fabricEditorEligibility' in contentNode &&
			fg('confluence_vp_header_frag_reader')
				? contentNode.fabricEditorEligibility
				: null;

		const contentHeader = isFabricPage ? (
			<FabricEligibilityFragmentProvider contentFragmentRef={fabricEligibilityFragmentRef}>
				<ContentTypesHeader
					contentId={contentId}
					contentFragmentRef={contentNode?.contentTypesHeader}
					editContentFragmentRef={contentNode?.editContentButton}
					shareContentFragmentRef={contentNode?.shareAndRestrictButtonData}
					publicLinkPage={resolvedPublicLinkPage}
					isConvertToFolderHighlighted={isContentToolsForcedOpen}
					setIsConvertToFolderHighlighted={setIsContentToolsForcedOpen}
				/>
			</FabricEligibilityFragmentProvider>
		) : (
			renderContentHeader({
				contentType,
				contentTitle,
				isFabricPage,
				loading,
				subType,
				lastModifiedDate,
				isThemed: spaceIsThemed,
				isContentToolsForcedOpen,
				setIsContentToolsForcedOpen,
				isArchived,
			})
		);

		const shouldShowAifcCreateBanner = isFabricPage;

		return (
			<FixedSpaceHeaderContainer
				isThemed={spaceIsThemed}
				data-testid="content-header-container"
				shouldHavePadding={!isFabricPage} // tiny pages need extra left and right padding in header since we show the old header for tiny pages
				shouldShowAifcCreateBanner={shouldShowAifcCreateBanner}
			>
				{shouldShowAifcCreateBanner ? (
					<Stack grow="fill">
						{contentHeader}
						<SmartCreateFeedbackBannerLoader contentType="page" />
					</Stack>
				) : (
					contentHeader
				)}
			</FixedSpaceHeaderContainer>
		);
	};

	const renderQueryChildren = ({
		loading,
		error,
		data,
	}: {
		loading: boolean;
		error?: Error;
		data?: ContentHeaderUnifiedQuery$data;
	}) => {
		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
				return null;
			} else {
				// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
				return <ErrorDisplay error={error} />;
			}
		} else if (!data) {
			return null;
		}

		const contentNode = data.content?.nodes?.[0];
		if (!contentNode) {
			return null;
		}

		return renderContentHeaderData({
			contentNode,
			spaceIsThemed: Boolean(data.space?.theme?.themeKey),
			resolvedPublicLinkPage: data.publicLinkPage,
			loading,
		});
	};

	const renderContentHeaderQuery = ({
		useEditButtonFragment,
		useAccessButtonFragment,
		includePublicLink,
	}: Pick<
		ContentHeaderUnifiedQuery$variables,
		'useEditButtonFragment' | 'useAccessButtonFragment' | 'includePublicLink'
	>) => (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			{showMetadataItems && (
				<PageSegmentLoadStart
					key={`PageSegmentLoadStart-connect-${contentId}`}
					metric={CONNECT_APP_WEB_FRAGMENT_METRIC}
				/>
			)}
			<ExperienceStart
				key={`object-header-${contentId}`}
				id={contentId}
				name={OBJECT_HEADER_EXPERIENCE}
			/>
			{expValEquals('cc-markdown-mode', 'isEnabled', true) ? (
				fg('confluence_vp_obj_header_frag_dedup') ? (
					<Query<ContentHeaderUnifiedQuery$data, ContentHeaderUnifiedQuery$variables>
						query={ContentHeaderUnifiedQuery}
						variables={{
							contentId,
							spaceKey,
							// Don't omit versionOverride, embeddedContentRender and limit
							// It's an Apollo bug that if preloader these needs to be match preloaded query
							// Also doesn't work if removing them from preloader variables HOT-109035
							versionOverride: versionOverride || null,
							embeddedContentRender: null,
							limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
							useNewContentTopper: true,
							isMarkdownModeEnabled: true,
							includeDevDocsBylineConnection: false,
							useObjectHeaderFragment: true,
							useEditButtonFragment,
							useAccessButtonFragment,
							includePublicLink,
						}}
						context={{ single: true }}
					>
						{renderQueryChildren}
					</Query>
				) : (
					<Query<ContentHeaderUnifiedQuery$data, ContentHeaderUnifiedQuery$variables>
						query={ContentHeaderUnifiedQuery}
						variables={{
							contentId,
							spaceKey,
							// Don't omit versionOverride, embeddedContentRender and limit
							// It's an Apollo bug that if preloader these needs to be match preloaded query
							// Also doesn't work if removing them from preloader variables HOT-109035
							versionOverride: versionOverride || null,
							embeddedContentRender: null,
							limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
							useNewContentTopper: true,
							isMarkdownModeEnabled: true,
							includeDevDocsBylineConnection: false,
							useObjectHeaderFragment: false,
							useEditButtonFragment,
							useAccessButtonFragment,
							includePublicLink,
						}}
						context={{ single: true }}
					>
						{renderQueryChildren}
					</Query>
				)
			) : fg('confluence_vp_obj_header_frag_dedup') ? (
				<Query<ContentHeaderUnifiedQuery$data, ContentHeaderUnifiedQuery$variables>
					query={ContentHeaderUnifiedQuery}
					variables={{
						contentId,
						spaceKey,
						// Don't omit versionOverride, embeddedContentRender and limit
						// It's an Apollo bug that if preloader these needs to be match preloaded query
						// Also doesn't work if removing them from preloader variables HOT-109035
						versionOverride: versionOverride || null,
						embeddedContentRender: null,
						limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
						useNewContentTopper: true,
						isMarkdownModeEnabled: false,
						includeDevDocsBylineConnection: false,
						useObjectHeaderFragment: true,
						useEditButtonFragment,
						useAccessButtonFragment,
						includePublicLink,
					}}
					context={{ single: true }}
				>
					{renderQueryChildren}
				</Query>
			) : (
				<Query<ContentHeaderUnifiedQuery$data, ContentHeaderUnifiedQuery$variables>
					query={ContentHeaderUnifiedQuery}
					variables={{
						contentId,
						spaceKey,
						versionOverride: versionOverride || null,
						embeddedContentRender: null,
						limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
						useNewContentTopper: true,
						isMarkdownModeEnabled: false,
						includeDevDocsBylineConnection: false,
						useObjectHeaderFragment: false,
						useEditButtonFragment,
						useAccessButtonFragment,
						includePublicLink,
					}}
					context={{ single: true }}
				>
					{renderQueryChildren}
				</Query>
			)}
		</ErrorBoundary>
	);

	if (useDocumentContract && fg('confluence_vp_header_frag_reader')) {
		if (!contentFragmentRef || !spaceFragmentRef) {
			return null;
		}

		return (
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				{showMetadataItems && (
					<PageSegmentLoadStart
						key={`PageSegmentLoadStart-connect-${contentId}`}
						metric={CONNECT_APP_WEB_FRAGMENT_METRIC}
					/>
				)}
				<ExperienceStart
					key={`object-header-${contentId}`}
					id={contentId}
					name={OBJECT_HEADER_EXPERIENCE}
				/>
				<ContentHeaderFragmentReader
					contentFragmentRef={contentFragmentRef}
					spaceFragmentRef={spaceFragmentRef}
				>
					{({ contentNode, space }) =>
						renderContentHeaderData({
							contentNode,
							spaceIsThemed: Boolean(space.theme?.themeKey),
							resolvedPublicLinkPage: publicLinkPage,
							loading: false,
						})
					}
				</ContentHeaderFragmentReader>
			</ErrorBoundary>
		);
	}

	if (fg('confluence_vp_edit_frag_reader')) {
		if (fg('confluence_vp_access_frag_reader')) {
			return renderContentHeaderQuery({
				useEditButtonFragment: true,
				useAccessButtonFragment: true,
				includePublicLink: isContentTypePublicLinkEligible,
			});
		}

		return renderContentHeaderQuery({
			useEditButtonFragment: true,
			useAccessButtonFragment: false,
			includePublicLink: false,
		});
	}

	if (fg('confluence_vp_access_frag_reader')) {
		return renderContentHeaderQuery({
			useEditButtonFragment: false,
			useAccessButtonFragment: true,
			includePublicLink: isContentTypePublicLinkEligible,
		});
	}

	return renderContentHeaderQuery({
		useEditButtonFragment: false,
		useAccessButtonFragment: false,
		includePublicLink: false,
	});
};
